import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { SelectDeviceProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import RadioButtons from '../../components/RadioButtons';
import i18next from 'i18next';
import { CheckmarkPasswordToggle } from '../../assets/Icons';
import { Password } from '../Password';
import ExplanationOverlay from '../../components/ExplanationOverlay';
import {
	ContentsResponse, createPollingLoop,
	fetchDeliveryAccessToken,
	getBackoffDelay,
	refreshAccessToken,
} from '../../utils/pairingHelpers';
import { useTimeout } from '../../utils/useTimeout';

type Activation = {
	id_token: string;
	refresh_token: string;
	token_type?: string;
} | null;

export default function SelectDevice(props: SelectDeviceProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);
	const [individualOrKvps, setIndividualOrKvps] = useState(
		state.userPassword ? 'individual' : 'kvps'
	);
	const [passwordToggle, setPasswordToggle] = useState(false);
	const [title, setTitle] = useState<string>('');
	const [vin, setVin] = useState<string>('');
	const [showDeletePasswordWarning, setShowDeletePasswordWarning] =
		useState(false);
	const [status, setStatus] = useState<string>('');
	const deliveryUrl =
		state.market?.urls?.delivery ||
		state.market?.qsUrl ||
		'';
	const deliveryBase = deliveryUrl
		? deliveryUrl.endsWith('/module/delivery/bpca')
			? deliveryUrl
			: `${deliveryUrl.replace(/\/$/, '')}/module/delivery/bpca`
		: '';
	const idToken = state.pinpairingActivationResponse.id_token || '';
	const refreshToken = state.pinpairingActivationResponse.refresh_token || '';
	const activation: Activation | null =
		idToken && refreshToken
			? {
					id_token: idToken,
					refresh_token: refreshToken,
					token_type: 'Bearer',
				}
			: null;
	const stopRef = useRef(false);
	const contentsTimer = useTimeout();

	useEffect(() => {
		if (typeof state.goToLabel === 'undefined') {
			dispatch({ type: 'SET_GO_TO_LABEL', value: false });
		}
	}, []);

	useEffect(() => {
		stopRef.current = false;

		const run = async () => {
			if (!deliveryBase) {
				setStatus('No delivery url configured.');
				return;
			}

			if (!activation) {
				setStatus('No device paired.');
				return;
			}

			let token = await fetchDeliveryAccessToken(
				deliveryBase,
				activation.id_token,
				activation.refresh_token,
				stopRef,
				status => {
					if (status === 404)
						setStatus('Wait for pairing/delivery token missing…');
					if (status === 429)
						setStatus('reached rate limit, wait…');
				}
			);

			if (!token || stopRef.current) {
				setStatus('Error while fetching delivery token.');
				return;
			}

			localStorage.setItem('accesstoken', token);

			const initialUrl =
				`${deliveryBase}/api/v2/contents?fields-preset=full?device_id=${state.device}`;

			const resp = await fetch(initialUrl, {
				method: 'GET',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					Authorization: `Bearer ${token}`,
				},
			});

			const response: { Pagination?: { Total?: number } } =
				await resp.clone().json();

			const limit = response.Pagination?.Total || 1000;

			localStorage.setItem('contents_limit', String(limit));

			const contentsUrlWithLimit =
				`${deliveryBase}/api/v2/contents?fields-preset=full&limit=${limit}&device_id=${state.device}`;

			let didRefreshOnceFor401 = false;

			const poll = createPollingLoop();

			poll<ContentsResponse>({
				url: contentsUrlWithLimit,
				accessToken: token,

				stopRef,

				maxDelay: 15000,
				timerSet: contentsTimer.set,

				getDelay: getBackoffDelay,

				parseBody: async (resp) => {
					try {
						return (await resp.clone().json()) as ContentsResponse;
					} catch {
						return undefined;
					}
				},

				shouldRetry: (status) =>
					status === 204 || status === 404 || status === 429,

				onSuccess: async (_, body) => {
					if (body?.RootContent && Array.isArray(body?.Contents)) {
						const rootItem = body.Contents.find(
							(it: { Uid: string }) => it?.Uid === body.RootContent
						);

						const t =
							rootItem?.ContentFieldValues?.title?.['de-DE'] ||
							rootItem?.ContentFieldValues?.title?.['en-GB'] ||
							'';

						const v = rootItem?.ContentFieldValues?.vin || '';

						setTitle(t || '');
						setVin(v || '');
						setStatus('');
					} else {
						setStatus('No content available.');
					}
				},

				on401: async () => {
					if (didRefreshOnceFor401) return null;
					didRefreshOnceFor401 = true;

					const { accessToken } = await refreshAccessToken(
						deliveryBase,
						activation.id_token,
						activation.refresh_token
					);

					if (accessToken) {
						token = accessToken;
						return accessToken;
					}

					return null;
				},
			});
		};

		run();

		return () => {
			stopRef.current = true;
			contentsTimer.clear();
		};
	}, [deliveryBase, activation?.id_token, activation?.refresh_token]);

	const isSubmittingRef = useRef(false);

	const goFinal = () => {
		if (isSubmittingRef.current) return;
		isSubmittingRef.current = true;

		try {

			requestAnimationFrame(() => {
				dispatch({ type: 'SET_SCREEN', value: ScreenEnum.final });
				isSubmittingRef.current = false;
			});
		} catch (e) {
			console.error('[SelectDevice] Error on confirming', e);
			isSubmittingRef.current = false;
		}
	};

	const individualPasswordClickHandler = () => setPasswordToggle(true);
	const kvpsPasswordClickHandler = () => {
		if (state.userPassword) setShowDeletePasswordWarning(true);
	};

	return (
		<>
			<div className='top'>
				<div
					className={`select-device ${passwordToggle ? 'opacity' : ''}`}
				>
					{props.backBtn(ScreenEnum.selectMarket)}
					<h1>{t('SelectDeviceHeadline')}</h1>
					{state.pinpairingActivationResponse.id_token &&
					/^\d{3}$/.test(
						state.pinpairingActivationResponse.id_token
					) ? (
						<p className='p1 error-message'>
							{t(
								'SelectDeviceError' +
									state.pinpairingActivationResponse.id_token
							)}
						</p>
					) : (
						<p className='p1'>{t('SelectDeviceText')}</p>
					)}
					<Progress
						screen={ScreenEnum.selectDevice}
						setScreen={props.setScreen}
					/>
					<h3>Your device will show the following pricesheet:</h3>
					{!!title && <h3>{title}</h3>}
					{!!vin && <h3>{vin}</h3>}
					{!!status && <p className='p2'>{status}</p>}
					{!state.apk && (
						<RadioButtons
							options={[
								{
									selected: !state.goToLabel,
									text: t('SetDeviceShowPricesheet'),
									value: false,
								},
								{
									selected: state.goToLabel,
									text: t('SetDeviceShowLabel'),
									value: true,
								},
							]}
							onClick={(value: boolean) => {
								dispatch({ type: 'SET_GO_TO_LABEL', value });
							}}
						/>
					)}
					<div
						className={`select-password ${state.apk ? 'apk' : ''}`}
					>
						<p className='bold'>
							{i18next.t('SelectPasswordTitle')}
						</p>
						<p>
							{individualOrKvps === 'kvps'
								? i18next.t('SelectPasswordKvps')
								: i18next.t('SelectPasswordIndividual')}
						</p>
					</div>
					<div
						className={`password-toggle-wrapper ${state.apk ? 'apk' : ''}`}
					>
						<div className='password-toggle'>
							<button
								className={`${individualOrKvps === 'individual' ? 'active' : ''}`}
								type='button'
								onClick={individualPasswordClickHandler}
							>
								<div>
									<p>
										{individualOrKvps === 'individual'
											? i18next.t(
													'IndividualPasswordAsPassword'
												)
											: i18next.t('IndividualPassword')}
									</p>
									<p>
										{individualOrKvps === 'individual' &&
											CheckmarkPasswordToggle}
									</p>
								</div>
							</button>
							<button
								className={`${individualOrKvps === 'kvps' ? 'active' : ''}`}
								type='button'
								onClick={kvpsPasswordClickHandler}
							>
								<div>
									<p>
										{individualOrKvps === 'kvps'
											? i18next.t('KVPSAsPassword')
											: i18next.t('KvpsPassword')}
									</p>
									<p>
										{individualOrKvps === 'kvps' &&
											CheckmarkPasswordToggle}
									</p>
								</div>
							</button>
						</div>
					</div>
				</div>
			</div>
			{!passwordToggle && (
				<div className='buttons'>
					<button className='white' onClick={goFinal}>
						{t('ButtonConfirm')}
					</button>
					{props.cancelBtn}
				</div>
			)}
			{passwordToggle && (
				<Password
					passwordToggle={() => setPasswordToggle(false)}
					saveBtn={() => {
						setPasswordToggle(false);
						setIndividualOrKvps('individual');
					}}
					setkvps={() => setIndividualOrKvps('kvps')}
				/>
			)}
			{showDeletePasswordWarning && (
				<ExplanationOverlay
					button={() => {
						setShowDeletePasswordWarning(false);
						dispatch({ type: 'SET_USER_PASSWORD', value: null });
						dispatch({
							type: 'SET_SCREEN',
							value: ScreenEnum.selectDevice,
						});
						setIndividualOrKvps('kvps');
					}}
					cancel={() => setShowDeletePasswordWarning(false)}
					showButton={true}
					title={i18next.t('DeletePassword')}
					text={'DeletePasswordsTexts'}
				/>
			)}
		</>
	);
}
